/**
 * M365 licence consumption — where the licence money is going, and where it
 * is going nowhere.
 *
 * Everything on this page is measured against the tenant through Microsoft
 * Graph. Nothing is seeded, and nothing is estimated except cost, which is
 * derived from rates an operator configured and is labelled as such wherever
 * it appears.
 *
 * **Privacy.** Every panel here is an aggregate. No individual is named on
 * this page at any point, whatever the viewer's role — the identity-level
 * drill-down is a separate, admin-gated route, and the backend enforces that
 * rather than trusting this component to hide anything.
 */

import { Alert, PageHeader, Skeleton } from "@norican/react";

import { useLicenseOverview, useMe } from "@/lib/api";

import { DataFreshness } from "./data-freshness";
import { GraceRiskPanel } from "./grace-risk-panel";
import { InactivityHistogram } from "./inactivity-histogram";
import { LicenceKpiRow } from "./kpi-row";
import { ReclaimableSummary } from "./reclaimable-summary";
import { RenewalRisk } from "./renewal-risk";
import { SeatSplitDonut } from "./seat-split-donut";
import { SeatUtilisation } from "./seat-utilisation";
import { ServicePlanWaste } from "./service-plan-waste";
import { SkuWasteChart } from "./sku-waste-chart";
import { UsageReportPanel } from "./usage-report-blocked";
import { ZeroAssignment } from "./zero-assignment";

export { ReclaimableAccountsPage } from "./reclaimable-accounts";
export { RECLAIMABLE_ACCOUNTS_PATH } from "./reclaimable-summary";

export function LicensesPage() {
  const { data, isPending, error } = useLicenseOverview();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Licence consumption"
        subtitle="Microsoft 365 seats bought, seats used, and the spend sitting idle between the two."
      />

      {error && (
        <Alert tone="danger" title="Couldn't load licence data">
          {error instanceof Error ? error.message : "The licence API did not respond."}
        </Alert>
      )}

      {isPending && !data ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="7rem" />
            ))}
          </div>
          <Skeleton height="18rem" />
          <Skeleton height="18rem" />
        </div>
      ) : (
        data && (
          <>
            <DataFreshness
              configured={data.configured}
              statusDetail={data.status_detail}
              namesConcealed={data.names_concealed}
              generatedAt={data.generated_at}
            />

            {!data.configured ? (
              /* No Azure credential, or a scope that hasn't been granted. The
                 board says exactly what is missing rather than rendering a
                 grid of confident zeroes. */
              <Alert tone="warning" title="Licence data is not available yet">
                {data.status_detail}
              </Alert>
            ) : (
              <>
                <LicenceKpiRow totals={data.totals} />

                {data.data_quality.length > 0 && (
                  <Alert tone="warning" title="Data quality">
                    <ul className="list-disc pl-5">
                      {data.data_quality.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <SeatSplitDonut
                  totals={data.totals}
                  reclaimable={data.reclaimable}
                  isAdmin={isAdmin}
                  generatedAt={data.generated_at}
                />

                {/* People before money: an expiring grace period takes a
                    licence off somebody's account, which outranks idle spend. */}
                <GraceRiskPanel
                  rows={data.grace_at_risk}
                  totals={data.totals}
                  generatedAt={data.generated_at}
                />

                <SeatUtilisation skus={data.top_unused} />

                <SkuWasteChart
                  rows={data.top_unused}
                  currency={data.totals.currency}
                  pricesConfigured={data.totals.prices_configured}
                  generatedAt={data.generated_at}
                />

                {/* Idle seats are money; a suite whose premium workloads are
                    switched off is the same money on a cheaper plan. Sits
                    after the waste ranking because it is a downgrade
                    conversation, not a cancellation one. */}
                <ServicePlanWaste rows={data.service_plan_waste} generatedAt={data.generated_at} />

                <RenewalRisk rows={data.renewal_risk} />

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <ZeroAssignment
                    rows={data.zero_assignment}
                    currency={data.totals.currency}
                    pricesConfigured={data.totals.prices_configured}
                  />
                  <InactivityHistogram
                    buckets={data.reclaimable.inactivity_buckets}
                    signInDataAvailable={data.reclaimable.sign_in_data_available}
                    isAdmin={isAdmin}
                    generatedAt={data.generated_at}
                  />
                </div>

                <ReclaimableSummary summary={data.reclaimable} isAdmin={isAdmin} />

                <UsageReportPanel status={data.usage_reports} />
              </>
            )}
          </>
        )
      )}
    </main>
  );
}
